import { Callout } from '@carbon/react';
import styles from './duplicate-well-banner.module.scss';

export interface DuplicateWellBannerProps {
  /**
   * How many open duplicates the detector found for the scoped well, as it counted
   * them. Read straight off the candidates endpoint rather than from the pool, so the
   * banner can stand before the pool behind it has been fetched.
   */
  duplicateCount: number;
  onReconcile: () => void;
}

/**
 * The entry point to Reconcile Wells: a standing advisory that the scoped well has
 * records that look like duplicates of it.
 *
 * A Callout rather than an ActionableNotification: the banner carries a button, which
 * InlineNotification refuses, but it is a standing advisory rather than an alert, so it
 * must not take focus. ActionableNotification always does — it deprecated `hasFocus`
 * and points at Callout for exactly this case.
 * @param {DuplicateWellBannerProps} props The duplicate count and the open handler.
 * @return {JSX.Element | null} The banner, or nothing when there is nothing to reconcile.
 */
export function DuplicateWellBanner({
  duplicateCount,
  onReconcile,
}: DuplicateWellBannerProps) {
  if (duplicateCount < 1) return null;

  return (
    <Callout
      className={styles.banner}
      kind="info"
      lowContrast
      title="Potential duplicate"
      subtitle={
        duplicateCount === 1
          ? '1 record looks like a duplicate of this well.'
          : `${duplicateCount} records look like duplicates of this well.`
      }
      actionButtonLabel="Reconcile duplicates"
      onActionButtonClick={onReconcile}
    />
  );
}
